import PropTypes from 'prop-types';
import { useState, useMemo } from 'react';
import styled, { css, useTheme } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { Card } from '@hivebrite/design-system/Card';
import {
  FontAwesomeIcon,
  CATEGORIES,
} from '@hivebrite/design-system/FontAwesomeIcon';
import { Image } from '@hivebrite/design-system/Image';
import { LinkSimple } from '@hivebrite/design-system/Link';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';

import * as PostTypes from 'hb-react/frontoffice/apps/LiveFeed/constants/PostTypes';
import { useLiveFeedContext } from 'hb-react/frontoffice/apps/LiveFeed/contexts/LiveFeedContext';
import getContentTypesUserCanCreate from 'hb-react/frontoffice/apps/LiveFeed/utils/getContentTypesUserCanCreate';
import CheckInFormModal from 'hb-react/frontoffice/components/CheckInFormModal';
import TripFormModal from 'hb-react/frontoffice/components/TripFormModal';
import UserPostFormModal from 'hb-react/frontoffice/components/UserPostFormModal';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import useInputAndDropdownTheme from 'hb-react/shared/hooks/theme/useInputAndDropdownTheme';
import useOutboxedFontTheme from 'hb-react/shared/hooks/theme/useOutboxedFontTheme';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import onEnterOrSpacePress from 'hb-react/shared/utils/onEnterOrSpacePress';
import ThemeProvider from 'hb-react/style/ThemeProvider';

const CreateContentIcon = (props) => (
  <FontAwesomeIcon iconType={CATEGORIES.SOLID} {...props} height="16px" />
);

const CheckInIcon = (props) => (
  <CreateContentIcon iconName="location-dot" {...props} />
);
const EventIcon = (props) => (
  <CreateContentIcon iconName="calendar-days" {...props} />
);
const TripIconOld = (props) => (
  <CreateContentIcon iconName="plane" rotation={-30} {...props} />
);
const TripIcon = (props) => (
  <CreateContentIcon iconName="globe-stand" {...props} />
);

const CreateContent = ({ onCreate }) => {
  const outboxedFontTheme = useOutboxedFontTheme();
  const inputAndDropdownTheme = useInputAndDropdownTheme();
  const { flagGoogleMapsComponent, flagEvUserPicturesToGlobalStorage } =
    useFeatureFlag();
  const { currentUser, currentNetwork } = useDefaultAppContexts();
  const {
    config: { type: configType, topic },
  } = useLiveFeedContext();
  const theme = useTheme();

  const [displayCreateUserPostForm, setDisplayCreateUserPostForm] =
    useState(false);
  const [displayCreateCheckInForm, setDisplayCreateCheckInForm] =
    useState(false);
  const [displayCreateTripForm, setDisplayCreateTripForm] = useState(false);

  const {
    [PostTypes.POST]: postContent,
    [PostTypes.EVENT]: eventContent,
    [PostTypes.TRIP]: tripContent,
    [PostTypes.CHECKIN]: checkInContent,
  } = getContentTypesUserCanCreate({
    currentNetwork,
    currentUser,
    configType,
  });
  const otherContentCreatableAvailable = useMemo(
    () =>
      tripContent.available ||
      checkInContent.available ||
      eventContent.available,
    [tripContent, checkInContent, eventContent],
  );

  return (
    <>
      <ScreenReadersContent>
        <h2>{__('Create a post')}</h2>
      </ScreenReadersContent>
      {postContent.available && (
        <Card p={0} mb={4} mx={{ _: 4, m: 0 }}>
          <Box
            py={6}
            px={{ _: 4, m: 6 }}
            alignItems="center"
            width={{ _: '100%', l: '86%', xl: '100%' }}
          >
            <AvatarWrapper>
              <Image
                src={
                  flagEvUserPicturesToGlobalStorage
                    ? currentUser.new_photo?.thumbnail_url
                    : currentUser.photo_thumb_url
                }
                alt=""
              />
            </AvatarWrapper>
            <Box
              css="flex: 1 1 0%;"
              tabIndex={0}
              onClick={() => setDisplayCreateUserPostForm(true)}
              onKeyPress={onEnterOrSpacePress(() =>
                setDisplayCreateUserPostForm(true),
              )}
              role="button"
              aria-label={s__(
                'LiveFeedAccessibility|Open the dialog window to write a post',
              )}
            >
              <Box
                aria-hidden="true"
                css={css`
                  border-radius: ${theme.input.borderRadius}px;
                  border: 1px solid ${inputAndDropdownTheme.borderColor};
                `}
                bg={inputAndDropdownTheme.bg}
                // Hardcode color since it's a fake input - taking input placeholder color
                color={inputAndDropdownTheme.placeholder}
                font="body-regular-dove"
                height={54}
                p="11px"
                width="100%"
                data-testid="open-write-post-modal"
              >
                {__('Write a post')}
              </Box>
            </Box>
          </Box>
        </Card>
      )}

      {otherContentCreatableAvailable && (
        <Box
          pl={{ _: 4, m: 0 }}
          display="block"
          data-testid="create-other-content"
        >
          <ThemeProvider
            theme="fo"
            fontBrightness={outboxedFontTheme.fontBrightness}
          >
            <Box font="h3-semibold">{__('Share a new...')}</Box>
          </ThemeProvider>
          <Box as="ul" flexWrap={{ _: 'wrap', m: 'nowrap' }}>
            {checkInContent.available && (
              <li>
                <Button
                  size="large"
                  mr={4}
                  mt={4}
                  icon={CheckInIcon}
                  onClick={() => setDisplayCreateCheckInForm(true)}
                  variant="inversed"
                >
                  {__('Check-in')}
                </Button>
              </li>
            )}
            {tripContent.available && (
              <li>
                <Button
                  size="large"
                  mr={4}
                  mt={4}
                  icon={flagGoogleMapsComponent ? TripIcon : TripIconOld}
                  onClick={() => setDisplayCreateTripForm(true)}
                  variant="inversed"
                >
                  {__('Trip')}
                </Button>
              </li>
            )}
            {eventContent.available && (
              <li>
                {/* Redirect to event form page */}
                <LinkSimple
                  to={Routes.user_new_user_event_path(currentUser.id, {
                    context: 'feed',
                  })}
                >
                  <Button
                    size="large"
                    mr={4}
                    mt={4}
                    icon={EventIcon}
                    variant="inversed"
                  >
                    {__('Event')}
                  </Button>
                </LinkSimple>
              </li>
            )}
          </Box>
        </Box>
      )}

      {displayCreateUserPostForm && (
        <UserPostFormModal
          isOpen={displayCreateUserPostForm}
          onCloseModal={() => setDisplayCreateUserPostForm(false)}
          onCreate={onCreate}
          topic={topic}
          type={configType}
        />
      )}
      {displayCreateCheckInForm && (
        <CheckInFormModal
          onCreate={onCreate}
          onCloseModal={() => setDisplayCreateCheckInForm(false)}
        />
      )}
      {displayCreateTripForm && (
        <TripFormModal
          onCreate={onCreate}
          onCloseModal={() => setDisplayCreateTripForm(false)}
        />
      )}
    </>
  );
};

CreateContent.propTypes = {
  onCreate: PropTypes.func,
};

CreateContent.defaultProps = {
  selected: false,
};

export default CreateContent;

const AvatarWrapper = styled(Box).attrs(() => ({
  height: { _: 48, m: 60 },
  mr: 3,
  width: { _: 48, m: 60 },
}))`
  user-select: none;

  img {
    border-radius: 50%;
    width: 100%;
  }
`;
